import React, { useEffect, useState } from "react";
import { api, fmtINR, fmtDate, formatErr } from "@/lib/api";
import { toast } from "sonner";
import { Plus, Factory, Trash } from "@phosphor-icons/react";

export default function Manufacturing() {
  const [tab, setTab] = useState("bom");
  const [boms, setBoms] = useState([]);
  const [orders, setOrders] = useState([]);
  const [products, setProducts] = useState([]);
  const [warehouses, setWarehouses] = useState([]);
  const [showBom, setShowBom] = useState(false);
  const [showMo, setShowMo] = useState(false);
  const [bomForm, setBomForm] = useState({
    finished_product_id: "", name: "", labour_cost: 0, overhead_cost: 0,
    components: [{ product_id: "", quantity: 1 }],
  });
  const [moForm, setMoForm] = useState({
    bom_id: "", quantity: 1, warehouse_id: "", date: new Date().toISOString().slice(0,10),
  });

  const load = async () => {
    const [b, o, p, w] = await Promise.all([
      api.get("/mfg/bom"), api.get("/mfg/orders"),
      api.get("/products"), api.get("/erp/warehouses"),
    ]);
    setBoms(b.data); setOrders(o.data); setProducts(p.data); setWarehouses(w.data);
  };
  useEffect(() => { load(); }, []);

  const setComp = (i, patch) => setBomForm({ ...bomForm, components: bomForm.components.map((c, idx) => idx === i ? { ...c, ...patch } : c) });
  const addComp = () => setBomForm({ ...bomForm, components: [...bomForm.components, { product_id: "", quantity: 1 }] });
  const removeComp = (i) => setBomForm({ ...bomForm, components: bomForm.components.length > 1 ? bomForm.components.filter((_,idx) => idx !== i) : bomForm.components });

  const submitBom = async (e) => {
    e.preventDefault();
    try {
      await api.post("/mfg/bom", { ...bomForm,
        labour_cost: Number(bomForm.labour_cost), overhead_cost: Number(bomForm.overhead_cost),
        components: bomForm.components.map((c) => ({ ...c, quantity: Number(c.quantity) })),
      });
      toast.success("BOM created");
      setShowBom(false);
      setBomForm({ finished_product_id: "", name: "", labour_cost: 0, overhead_cost: 0, components: [{ product_id: "", quantity: 1 }] });
      load();
    } catch (err) { toast.error(formatErr(err.response?.data?.detail)); }
  };
  const submitMo = async (e) => {
    e.preventDefault();
    try {
      const { data } = await api.post("/mfg/orders", { ...moForm, quantity: Number(moForm.quantity) });
      toast.success(`Produced ${data.quantity} units · Stock updated`);
      setShowMo(false);
      load();
    } catch (err) { toast.error(formatErr(err.response?.data?.detail)); }
  };

  return (
    <div className="space-y-5 lb-fade" data-testid="mfg-page">
      <div className="flex items-end justify-between">
        <div>
          <div className="lb-section-head">Manufacturing</div>
          <h1 className="font-display font-extrabold text-3xl tracking-tight">Bill of Materials & Production</h1>
          <p className="text-neutral-600 text-sm mt-1">Define BOMs · Create manufacture orders → auto-consume raw materials, produce finished goods, update stock.</p>
        </div>
        <div className="flex gap-2">
          <button className="lb-btn lb-btn-ghost" onClick={() => setShowBom((s) => !s)} data-testid="add-bom-btn">
            <Plus size={14} /> New BOM
          </button>
          <button className="lb-btn lb-btn-accent" onClick={() => setShowMo((s) => !s)} data-testid="produce-btn">
            <Factory size={14} /> Produce
          </button>
        </div>
      </div>

      {showBom && (
        <form onSubmit={submitBom} className="lb-card p-6 space-y-3">
          <div className="grid grid-cols-4 gap-3">
            <input required className="lb-input col-span-2" placeholder="BOM name (e.g. Chair Assembly v1)" value={bomForm.name} onChange={(e) => setBomForm({ ...bomForm, name: e.target.value })} />
            <select required className="lb-select" value={bomForm.finished_product_id} onChange={(e) => setBomForm({ ...bomForm, finished_product_id: e.target.value })}>
              <option value="">Finished product</option>
              {products.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
            </select>
            <div className="flex gap-2">
              <div className="flex-1"><label className="lb-label">Labour ₹</label><input type="number" className="lb-input font-mono text-right" value={bomForm.labour_cost} onChange={(e) => setBomForm({ ...bomForm, labour_cost: e.target.value })} /></div>
              <div className="flex-1"><label className="lb-label">Overhead ₹</label><input type="number" className="lb-input font-mono text-right" value={bomForm.overhead_cost} onChange={(e) => setBomForm({ ...bomForm, overhead_cost: e.target.value })} /></div>
            </div>
          </div>
          <div className="border border-neutral-200 p-3 bg-neutral-50 space-y-2">
            <div className="text-[11px] uppercase tracking-widest text-neutral-500 font-bold">Components</div>
            {bomForm.components.map((c, i) => (
              <div key={i} className="flex gap-2">
                <select required className="lb-select flex-1" value={c.product_id} onChange={(e) => setComp(i, { product_id: e.target.value })}>
                  <option value="">Raw material</option>
                  {products.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
                </select>
                <input type="number" step="0.01" required className="lb-input w-32 font-mono text-right" placeholder="Qty per unit" value={c.quantity} onChange={(e) => setComp(i, { quantity: e.target.value })} />
                <button type="button" onClick={() => removeComp(i)} className="text-neutral-500 hover:text-rose-600 px-2"><Trash size={14} /></button>
              </div>
            ))}
            <button type="button" onClick={addComp} className="lb-btn lb-btn-ghost lb-btn-sm"><Plus size={12} /> Component</button>
          </div>
          <div className="flex gap-2"><button className="lb-btn">Save BOM</button><button type="button" className="lb-btn lb-btn-ghost" onClick={() => setShowBom(false)}>Cancel</button></div>
        </form>
      )}

      {showMo && (
        <form onSubmit={submitMo} className="lb-card p-6 grid grid-cols-4 gap-3">
          <select required className="lb-select col-span-2" value={moForm.bom_id} onChange={(e) => setMoForm({ ...moForm, bom_id: e.target.value })}>
            <option value="">Select BOM</option>
            {boms.map((b) => <option key={b.id} value={b.id}>{b.name} → {b.finished_product_name}</option>)}
          </select>
          <select required className="lb-select" value={moForm.warehouse_id} onChange={(e) => setMoForm({ ...moForm, warehouse_id: e.target.value })}>
            <option value="">Warehouse</option>
            {warehouses.map((w) => <option key={w.id} value={w.id}>{w.name}</option>)}
          </select>
          <input type="number" step="0.01" required className="lb-input font-mono text-right" placeholder="Quantity to produce" value={moForm.quantity} onChange={(e) => setMoForm({ ...moForm, quantity: e.target.value })} />
          <input type="date" required className="lb-input" value={moForm.date} onChange={(e) => setMoForm({ ...moForm, date: e.target.value })} />
          <div className="col-span-4 flex gap-2"><button className="lb-btn lb-btn-accent"><Factory size={14} /> Produce Now</button><button type="button" className="lb-btn lb-btn-ghost" onClick={() => setShowMo(false)}>Cancel</button></div>
        </form>
      )}

      <div className="lb-card">
        <div className="flex border-b border-neutral-200">
          {[{ k: "bom", l: `BOMs (${boms.length})` }, { k: "orders", l: `Manufacture Orders (${orders.length})` }].map((t) => (
            <button key={t.k} onClick={() => setTab(t.k)}
                    className={"px-4 py-3 text-sm font-semibold border-b-2 " + (tab === t.k ? "border-[#0A66C2]" : "border-transparent text-neutral-500")}
                    data-testid={`mfg-tab-${t.k}`}>{t.l}</button>
          ))}
        </div>
        {tab === "bom" ? (
          <div className="p-4 space-y-3">
            {boms.map((b) => (
              <div key={b.id} className="border border-neutral-200 p-3">
                <div className="flex items-center justify-between mb-2">
                  <div>
                    <div className="font-display font-bold">{b.name}</div>
                    <div className="text-xs text-neutral-500">Produces: <b>{b.finished_product_name}</b></div>
                  </div>
                  <div className="text-xs font-mono text-neutral-600">
                    Labour: {fmtINR(b.labour_cost)} · Overhead: {fmtINR(b.overhead_cost)}
                  </div>
                </div>
                <table className="w-full text-sm">
                  <thead><tr className="text-[10px] uppercase text-neutral-500"><th className="text-left">Component</th><th className="text-right">Qty per unit</th></tr></thead>
                  <tbody>
                    {b.components.map((c, i) => <tr key={i}><td>{c.product_name}</td><td className="text-right font-mono">{c.quantity}</td></tr>)}
                  </tbody>
                </table>
              </div>
            ))}
            {!boms.length && <div className="text-center py-8 text-neutral-500">No BOMs. Create one to enable manufacturing.</div>}
          </div>
        ) : (
          <table className="lb-table">
            <thead><tr><th>Date</th><th>BOM</th><th className="text-right">Qty</th><th className="text-right">Labour</th><th className="text-right">Overhead</th><th>Status</th></tr></thead>
            <tbody>
              {orders.map((o) => (
                <tr key={o.id}>
                  <td className="text-sm">{fmtDate(o.date)}</td>
                  <td className="font-semibold">{o.bom_name}</td>
                  <td className="num">{o.quantity}</td>
                  <td className="amt">{fmtINR(o.labour_cost)}</td>
                  <td className="amt">{fmtINR(o.overhead_cost)}</td>
                  <td><span className="lb-badge lb-badge-success">{o.status}</span></td>
                </tr>
              ))}
              {!orders.length && <tr><td colSpan={6} className="text-center py-8 text-neutral-500">No production runs yet</td></tr>}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
